/**
 * The Recipes screen (tiered-test-authoring task 1.10).
 *
 * "Run" here goes through the REAL pipeline: `console/api/recipeRoutes.ts`
 * compiles the recipe to a `TestCase`, merges it into the console's `activeSet`,
 * then calls the SAME admission/policy/queue/start sequence `/api/tests/run`
 * uses. A result therefore appears in the real Runs tab and `GET /api/runs`, not
 * a side channel — see `recipeRoutes.ts`'s header for the full explanation of
 * what changed from an earlier version that spawned Playwright directly.
 *
 * A recipe is still a real, hand-editable YAML file checked in like any spec
 * (`recipes/*.yaml`) — the GUI step builder in "New recipe" (`NewRecipeModal`)
 * is a second way to PRODUCE that YAML, never a second way to run or store
 * it: "Save" still calls the exact same `POST /api/recipes` route the
 * Advanced/raw-YAML path always used, validated against the exact same
 * schema. "Compile + Run" reads whatever is on disk regardless of which path
 * created it.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, ApiError, type RecipeDetail, type RecipeRunResult, type RecipeSummary } from '../api';
import {
  RecipeStepBuilder,
  compileGuiSteps,
  recipeStepsToYaml,
  reverseMapRecipeSteps,
  type GuiStep,
} from './RecipeStepBuilder';

const NEW_RECIPE_TEMPLATE = `name: New recipe
description: What this recipe verifies.
application: player
mutating: false
steps:
  - functionName: goToPage
    params:
      application:
        kind: literal
        value: player
      path:
        kind: literal
        value: /
`;

export function RecipesScreen() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipes, setRecipes] = useState<RecipeSummary[] | null>(null);
  const [loadErrors, setLoadErrors] = useState<string[]>([]);
  // Pre-selected via `?open=<id>` — e.g. landing here right after a Tier 1
  // recording is promoted to a recipe, so the person reviewing it doesn't
  // have to go hunting for the one they just created.
  const [selectedId, setSelectedId] = useState<string | null>(() => searchParams.get('open'));
  const [detail, setDetail] = useState<RecipeDetail | null>(null);
  const [variant, setVariant] = useState<string>('');
  const [watch, setWatch] = useState(false);
  const [busy, setBusy] = useState<'compile' | 'run' | 'delete' | 'promote' | null>(null);
  const [compileResult, setCompileResult] = useState<{ testIds: string[]; preview: string[] } | null>(null);
  const [runResult, setRunResult] = useState<RecipeRunResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(() => {
    api.recipes()
      .then((r) => { setRecipes(r.recipes); setLoadErrors(r.errors); })
      .catch((e) => {
        // Recipes must never stay `null` on failure — that left this screen
        // showing "Loading recipes…" forever, with the real error computed
        // but never rendered (the loading gate below returns before the
        // error banner is ever reached).
        setRecipes([]);
        setLoadErrors([e instanceof Error ? e.message : String(e)]);
      });
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (searchParams.get('open')) setSearchParams({}, { replace: true });
    // Only ever consumed once, on arrival — never re-applied on subsequent renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setCompileResult(null);
    setRunResult(null);
    setError(null);
    setVariant('');
    api.recipe(selectedId).then(setDetail).catch(() => setDetail(null));
  }, [selectedId]);

  const compile = useCallback(async () => {
    if (!selectedId) return;
    setBusy('compile');
    setCompileResult(null);
    setRunResult(null);
    setError(null);
    try {
      setCompileResult(await api.compileRecipe(selectedId, variant || undefined));
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }, [selectedId, variant]);

  const run = useCallback(async () => {
    if (!selectedId) return;
    setBusy('run');
    setCompileResult(null);
    setRunResult(null);
    setError(null);
    try {
      const r = await api.runRecipe(selectedId, variant || undefined, 'qa', watch);
      setRunResult(r.runs);
      // Health is computed from real RunStore history (task 1.8/1.10) — refresh
      // the detail panel so the just-started run's effect on it is visible without
      // a manual reload, and refresh the list for the same reason.
      reload();
      api.recipe(selectedId).then(setDetail).catch(() => {});
      // Same navigation every other tier's "Run" button already does
      // (RunMatrix -> onRunStarted -> `/runs/:runId`) — a recipe run
      // previously only ever showed its outcome inline on this page, with no
      // way to watch it live even when a real run (with a real VNC url when
      // `watch` was on) was started.
      const firstRun = r.runs.find((run) => run.run)?.run;
      if (firstRun) navigate(`/runs/${firstRun.id}`);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(null);
    }
  }, [selectedId, variant, watch, reload, navigate]);

  const promote = useCallback(async () => {
    if (!selectedId) return;
    setBusy('promote');
    setError(null);
    try {
      await api.promoteRecipe(selectedId);
      reload();
      api.recipe(selectedId).then(setDetail).catch(() => {});
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [selectedId, reload]);

  const remove = useCallback(async (id: string, name: string) => {
    if (!window.confirm(`Delete "${name}"? This removes recipes/${id}.yaml and cannot be undone from here.`)) return;
    setBusy('delete');
    setError(null);
    try {
      await api.deleteRecipe(id);
      setSelectedId(null);
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  }, [reload]);

  if (!recipes) {
    return <p className="text-sm" style={{ color: 'hsl(var(--text-faint))' }}>Loading recipes…</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold">Recipes</h2>
          <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
            YAML-authored, composed, data-driven Playwright tests — checked in under{' '}
            <code>recipes/*.yaml</code>. Every recipe here is visible to every console user;
            there is no organization scoping or "publish" concept (design D4).
          </p>
        </div>
        <button className="btn btn-primary shrink-0 text-sm" onClick={() => setCreating(true)} data-testid="new-recipe">
          + New recipe
        </button>
      </div>

      {creating && (
        <NewRecipeModal
          existingRecipes={recipes ?? []}
          onClose={() => setCreating(false)}
          onCreated={(id) => { setCreating(false); reload(); setSelectedId(id); }}
        />
      )}

      {loadErrors.length > 0 && (
        <div className="rounded-lg border p-3 text-sm" style={{ borderColor: 'hsl(var(--danger))', color: 'hsl(var(--danger))' }}>
          <p className="font-medium">Some recipes did not validate against recipes/schema.json:</p>
          <ul className="mt-1 list-disc pl-5">
            {loadErrors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]">
        <ul className="flex flex-col gap-2">
          {recipes.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => setSelectedId(r.id)}
                data-testid={`recipe-${r.id}`}
                className="w-full rounded-lg border p-3 text-left transition"
                style={{
                  borderColor: selectedId === r.id ? 'hsl(var(--accent))' : 'hsl(var(--border))',
                  background: selectedId === r.id ? 'hsl(var(--bg-overlay))' : 'transparent',
                }}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">{r.name}</span>
                  <span className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>{r.application}</span>
                </div>
                {r.description && (
                  <p className="mt-1 text-xs" style={{ color: 'hsl(var(--text-muted))' }}>{r.description}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {r.credentialError && (
                    <span className="pill text-[11px]" style={{ color: 'hsl(var(--danger))' }} title={r.credentialError}>
                      missing credential
                    </span>
                  )}
                  {r.composeOnly && <span className="pill text-[11px]">compose-only</span>}
                  <span className="pill text-[11px]" style={{ color: r.promoted ? 'hsl(var(--success))' : 'hsl(var(--text-faint))' }}>
                    {r.promoted ? 'Tier 3 (nightly)' : 'Tier 2 only'}
                  </span>
                  {r.hasDataTable && <span className="pill text-[11px]">data-driven</span>}
                  {r.variants.length > 0 && <span className="pill text-[11px]">{r.variants.length} variant(s)</span>}
                  <HealthBadges health={r.health} />
                </div>
              </button>
            </li>
          ))}
        </ul>

        <div className="rounded-lg border p-4" style={{ borderColor: 'hsl(var(--border))' }}>
          {!detail ? (
            <p className="text-sm" style={{ color: 'hsl(var(--text-faint))' }}>Select a recipe to see its composition, health and run controls.</p>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold">{detail.recipe.name}</h3>
                  <p className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                    id: <code>{detail.recipe.id}</code> · application: {detail.recipe.application} · mutating: {String(detail.recipe.mutating ?? false)}
                  </p>
                </div>
                <div className="flex gap-2">
                  {detail.recipe.promoted ? (
                    <span className="pill text-xs" style={{ color: 'hsl(var(--success))' }}>
                      ✓ Promoted to Tier 3 — runs in the nightly
                    </span>
                  ) : (
                    <button
                      onClick={promote}
                      disabled={busy !== null || detail.stepCount === null}
                      data-testid="promote-recipe"
                      className="btn text-xs"
                      title={detail.stepCount === null ? 'Fix why this recipe cannot compile standalone before promoting it.' : undefined}
                    >
                      {busy === 'promote' ? 'Promoting…' : 'Promote to Tier 3 (run in nightly)'}
                    </button>
                  )}
                  <button
                    onClick={() => remove(detail.recipe.id, detail.recipe.name)}
                    disabled={busy !== null}
                    data-testid="delete-recipe"
                    className="btn text-xs"
                    style={{ color: 'hsl(var(--danger))' }}
                  >
                    {busy === 'delete' ? 'Deleting…' : 'Delete recipe'}
                  </button>
                </div>
              </div>
              {!detail.recipe.promoted && (
                <p className="text-xs" style={{ color: 'hsl(var(--text-muted))' }}>
                  Tier 2 only — this recipe can be run on demand from "Compile + Run" below, but will not run
                  unattended in the nightly/full regression until it is promoted to Tier 3.
                </p>
              )}

              <div className="text-sm">
                <p><strong>Composition order:</strong> {detail.compositionOrder.join(' → ') || detail.recipe.id}</p>
                {detail.stepCount !== null ? (
                  <p><strong>Compiled step count:</strong> {detail.stepCount}</p>
                ) : (
                  <p style={{ color: 'hsl(var(--text-muted))' }}>
                    Cannot compile standalone — {detail.compileError}
                  </p>
                )}
                {detail.recipe.dataTable && (
                  <p><strong>Data table:</strong> {detail.recipe.dataTable.rows.length} row(s), columns: {detail.recipe.dataTable.columns.join(', ')}</p>
                )}
              </div>

              <HealthPanel health={detail.health} />

              {(detail.recipe.variants?.length ?? 0) > 0 && (
                <label className="flex flex-col gap-1 text-sm">
                  <span>Variant</span>
                  <select
                    value={variant}
                    onChange={(e) => setVariant(e.target.value)}
                    className="rounded-md border px-2 py-1"
                    style={{ borderColor: 'hsl(var(--border))', background: 'transparent' }}
                  >
                    <option value="">(all variants)</option>
                    {detail.recipe.variants!.map((v) => (
                      <option key={v.name} value={v.name}>{v.name}</option>
                    ))}
                  </select>
                </label>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={watch}
                  onChange={(e) => setWatch(e.target.checked)}
                  data-testid="recipe-watch-toggle"
                />
                <span>Watch live (opens the run's VNC view)</span>
              </label>

              <div className="flex gap-2">
                <button
                  onClick={compile}
                  disabled={busy !== null || detail.stepCount === null}
                  data-testid="recipe-compile"
                  className="btn btn-ghost text-sm"
                >
                  {busy === 'compile' ? 'Compiling…' : 'Compile'}
                </button>
                <button
                  onClick={run}
                  disabled={busy !== null || detail.stepCount === null}
                  data-testid="recipe-run"
                  className="btn btn-primary text-sm"
                >
                  {busy === 'run' ? 'Running…' : 'Compile + Run'}
                </button>
              </div>

              {error && (
                <p className="text-sm" style={{ color: 'hsl(var(--danger))' }}>{error}</p>
              )}
              {compileResult && (
                <div className="text-sm">
                  <p className="font-medium">Registered {compileResult.testIds.length} TestCase(s) in the console's active set:</p>
                  <ul className="list-disc pl-5">{compileResult.testIds.map((id) => <li key={id}><code>{id}</code></li>)}</ul>
                  <p className="mt-1 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                    Preview of the compiled spec (not the execution path — see recipeRoutes.ts):{' '}
                    {compileResult.preview.map((w) => <code key={w}>{w}</code>)}
                  </p>
                </div>
              )}
              {runResult && (
                <div className="flex flex-col gap-2">
                  <p className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
                    Each result below is a REAL entry in the console's run history — open the Runs tab, or
                    <code> GET /api/runs</code>, to see the same run.
                  </p>
                  {runResult.map((r) => (
                    <div
                      key={r.testId}
                      className="rounded-lg border p-3 text-sm"
                      style={{
                        borderColor: r.run
                          ? r.run.status === 'passed' ? 'hsl(var(--success))' : r.run.status === 'failed' ? 'hsl(var(--danger))' : 'hsl(var(--border))'
                          : 'hsl(var(--danger))',
                      }}
                    >
                      <p className="font-medium"><code>{r.testId}</code></p>
                      {r.run && (
                        <p>
                          run <code>{r.run.id}</code> — status: {r.run.status}
                          {r.queued && ` (queued: ${r.reason})`}
                        </p>
                      )}
                      {r.error && <p style={{ color: 'hsl(var(--danger))' }}>{r.error} {r.refusedBy ? `(${r.refusedBy})` : ''}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** `$.steps[N]...` from a server validation error -> which GUI row(s) that compiled step came from. */
function mapErrorsToRows(
  errors: string[],
  guiSteps: GuiStep[],
  rowToStepIndex: Map<string, number | null>,
): { rowErrors: Map<string, string>; generalErrors: string[] } {
  const rowErrors = new Map<string, string>();
  const generalErrors: string[] = [];
  for (const err of errors) {
    const m = /\$\.steps\[(\d+)\]/.exec(err);
    const stepIndex = m ? Number(m[1]) : null;
    const matching = stepIndex === null ? [] : guiSteps.filter((r) => rowToStepIndex.get(r.id) === stepIndex);
    if (matching.length === 0) generalErrors.push(err);
    else for (const r of matching) rowErrors.set(r.id, err);
  }
  return { rowErrors, generalErrors };
}

const APPLICATIONS = ['player', 'studio', 'landing', 'docs', 'marketplace', 'api', 'cross-app'];

/**
 * Creates a real `recipes/<id>.yaml` file — either through the GUI step
 * builder (no YAML knowledge, no selectors required) or, via "Advanced",
 * by hand-writing YAML directly. Both paths end at the exact same
 * `POST /api/recipes` call, validated against the exact same schema, so
 * nothing invalid reaches disk either way.
 */
function NewRecipeModal({
  existingRecipes,
  onClose,
  onCreated,
}: {
  existingRecipes: RecipeSummary[];
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');
  const [id, setId] = useState('');
  const [name, setName] = useState('');
  const [application, setApplication] = useState('player');
  const [mutating, setMutating] = useState(false);
  const [guiSteps, setGuiSteps] = useState<GuiStep[]>([]);
  const [yaml, setYaml] = useState(NEW_RECIPE_TEMPLATE);

  const [boilerplateId, setBoilerplateId] = useState('');
  const [boilerplateBusy, setBoilerplateBusy] = useState(false);

  const [validation, setValidation] = useState<{ rowErrors: Map<string, string> | null; generalErrors: string[] } | null>(null);
  const [busy, setBusy] = useState<'validate' | 'save' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const useAsBoilerplate = async (sourceId: string) => {
    setBoilerplateId(sourceId);
    if (!sourceId) return;
    setBoilerplateBusy(true);
    setError(null);
    try {
      const detail = await api.recipe(sourceId);
      setName(`Copy of ${detail.recipe.name}`);
      setApplication(detail.recipe.application);
      setMutating(detail.recipe.mutating ?? false);

      const mapped = reverseMapRecipeSteps(detail.recipe.steps ?? [], detail.recipe.application);
      if (mapped) {
        setMode('simple');
        setGuiSteps(mapped);
      } else {
        // Something here (composition, a data table, control flow) the
        // simple builder cannot represent — the REAL file text, not a
        // reconstruction from the parsed object, so nothing about it is lost.
        const { yaml: rawYaml } = await api.recipeYaml(sourceId);
        setMode('advanced');
        setYaml(rawYaml);
      }
      setValidation(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBoilerplateBusy(false);
    }
  };

  const switchToAdvanced = () => {
    const { steps } = compileGuiSteps(application, guiSteps);
    setYaml(recipeStepsToYaml(name || 'New recipe', application, mutating, steps));
    setMode('advanced');
    setValidation(null);
  };

  const validate = async () => {
    setBusy('validate');
    setError(null);
    try {
      if (mode === 'simple') {
        const { steps, rowToStepIndex } = compileGuiSteps(application, guiSteps);
        const yamlText = recipeStepsToYaml(name || 'New recipe', application, mutating, steps);
        const result = await api.validateRecipeYaml(yamlText);
        const { rowErrors, generalErrors } = mapErrorsToRows(result.errors, guiSteps, rowToStepIndex);
        setValidation({ rowErrors, generalErrors });
      } else {
        const result = await api.validateRecipeYaml(yaml);
        setValidation({ rowErrors: null, generalErrors: result.errors });
      }
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const save = async () => {
    setBusy('save');
    setError(null);
    try {
      const yamlToSave = mode === 'simple'
        ? recipeStepsToYaml(name || 'New recipe', application, mutating, compileGuiSteps(application, guiSteps).steps)
        : yaml;
      const { recipe } = await api.createRecipe(id.trim(), yamlToSave);
      onCreated(recipe.id);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.message);
        const errors = (e as ApiError & { errors?: string[] }).errors ?? [];
        if (mode === 'simple') {
          const { rowToStepIndex } = compileGuiSteps(application, guiSteps);
          setValidation(mapErrorsToRows(errors, guiSteps, rowToStepIndex));
        } else {
          setValidation({ rowErrors: null, generalErrors: errors });
        }
      } else {
        setError(String(e));
      }
    } finally {
      setBusy(null);
    }
  };

  const canSave = id.trim() && (mode === 'simple' ? guiSteps.length > 0 : yaml.trim());

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto p-6"
      style={{ background: 'hsl(var(--bg) / 0.9)' }}
      onClick={onClose}
      data-testid="new-recipe-modal"
    >
      <div className="surface my-8 w-full max-w-4xl p-6" style={{ maxHeight: 'calc(100vh - 4rem)', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-baseline gap-3">
          <h2 className="text-base font-semibold">New recipe</h2>
          {mode === 'simple' && (
            <button className="btn btn-ghost ml-auto text-xs" onClick={switchToAdvanced} data-testid="new-recipe-advanced">
              Advanced: edit raw YAML instead
            </button>
          )}
          <button className={mode === 'simple' ? 'btn btn-ghost text-xs' : 'btn btn-ghost ml-auto text-xs'} onClick={onClose}>Close</button>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="flex flex-col gap-1 text-sm">
            <span>Recipe id (becomes <code>recipes/&lt;id&gt;.yaml</code>)</span>
            <input className="field" value={id} onChange={(e) => setId(e.target.value)} placeholder="e.g. reset-password" data-testid="new-recipe-id" />
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span>Start from an existing recipe (optional)</span>
            <select
              className="field"
              value={boilerplateId}
              onChange={(e) => void useAsBoilerplate(e.target.value)}
              disabled={boilerplateBusy}
              data-testid="new-recipe-boilerplate"
            >
              <option value="">— Start fresh —</option>
              {existingRecipes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
          </label>
        </div>

        {mode === 'simple' ? (
          <div className="mt-3 flex flex-col gap-3">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1 text-sm">
                <span>Name</span>
                <input className="field" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Create Course" data-testid="new-recipe-name" />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span>Application</span>
                <select className="field" value={application} onChange={(e) => setApplication(e.target.value)} data-testid="new-recipe-application">
                  {APPLICATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-2 text-sm">
                <span>&nbsp;</span>
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={mutating} onChange={(e) => setMutating(e.target.checked)} data-testid="new-recipe-mutating" />
                  Writes real data (mutating)
                </span>
              </label>
            </div>

            <RecipeStepBuilder
              application={application}
              rows={guiSteps}
              onChange={(rows) => { setGuiSteps(rows); setValidation(null); }}
              rowErrors={validation?.rowErrors ?? null}
              generalErrors={validation?.generalErrors ?? []}
            />
          </div>
        ) : (
          <label className="mt-3 flex flex-col gap-1 text-sm">
            <span>YAML</span>
            <textarea
              className="field font-mono text-xs"
              style={{ minHeight: '22rem' }}
              value={yaml}
              onChange={(e) => { setYaml(e.target.value); setValidation(null); }}
              data-testid="new-recipe-yaml"
            />
            {validation?.generalErrors.length ? (
              <ul className="mt-1 list-disc pl-5 text-xs" style={{ color: 'hsl(var(--danger))' }}>
                {validation.generalErrors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            ) : validation ? (
              <p className="text-xs" style={{ color: 'hsl(var(--success))' }}>✅ Valid</p>
            ) : null}
          </label>
        )}

        {error && (
          <div className="mt-3 rounded-lg border p-3 text-sm" style={{ borderColor: 'hsl(var(--danger))', color: 'hsl(var(--danger))' }}>
            <p className="font-medium">{error}</p>
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <button onClick={validate} disabled={busy !== null || !canSave} data-testid="new-recipe-validate" className="btn text-sm">
            {busy === 'validate' ? 'Validating…' : 'Validate'}
          </button>
          <button onClick={save} disabled={busy !== null || !canSave} data-testid="create-recipe" className="btn btn-primary text-sm">
            {busy === 'save' ? 'Saving…' : 'Save'}
          </button>
          <button onClick={onClose} className="btn text-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function HealthBadges({ health }: { health: RecipeSummary['health'] }) {
  return (
    <>
      {health.stale && <span className="pill text-[11px]" style={{ color: 'hsl(var(--warning))' }}>stale</span>}
      {health.likelyBroken && <span className="pill text-[11px]" style={{ color: 'hsl(var(--danger))' }}>likely broken</span>}
      {health.runCount > 0 && !health.stale && !health.likelyBroken && (
        <span className="pill text-[11px]" style={{ color: 'hsl(var(--success))' }}>
          {Math.round((health.passRate30d ?? 0) * 100)}% (30d)
        </span>
      )}
    </>
  );
}

function HealthPanel({ health }: { health: RecipeSummary['health'] }) {
  return (
    <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
      <Stat label="Runs (30d)" value={String(health.runCount)} />
      <Stat label="Pass rate (30d)" value={health.passRate30d === null ? '—' : `${Math.round(health.passRate30d * 100)}%`} />
      <Stat label="Avg duration" value={health.avgDurationMs === null ? '—' : `${(health.avgDurationMs / 1000).toFixed(1)}s`} />
      <Stat label="Flakiness" value={health.flakinessScore === null ? '—' : health.flakinessScore.toFixed(2)} />
      {health.stale && <Stat label="Flag" value="Stale — no run in 30 days" warn />}
      {health.likelyBroken && <Stat label="Flag" value="Likely broken — >50% failing" warn />}
    </div>
  );
}

function Stat({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="rounded-md border p-2" style={{ borderColor: 'hsl(var(--border))' }}>
      <p className="text-[11px]" style={{ color: 'hsl(var(--text-faint))' }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: warn ? 'hsl(var(--danger))' : 'hsl(var(--text))' }}>{value}</p>
    </div>
  );
}
